import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertCircle, Package } from 'lucide-react';
import { TimeFilter, PeriodType } from './TimeFilter';
import { UserBreakdownTable, ProductBreakdownTable } from './UserBreakdownTable';
import { PageHeader } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

interface DeviceStats {
  period: {
    start: string;
    end: string;
  };
  created_by_user: Array<{
    user_name: string;
    user_id: number | null;
    count: number;
  }>;
  total_created: number;
  created_by_product: Array<{
    product_name: string;
    product_id: number | null;
    count: number;
  }>;
  status_changes_from_active: Array<{
    current_status: string;
    count: number;
  }>;
  status_changes_by_user: Array<{
    user_name: string;
    user_id: number | null;
    new_status: string;
    count: number;
  }>;
}

const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'blocked': 'Blocked',
    'lost': 'Lost',
    'retired': 'Retired',
    'in_repair': 'In Repair',
    'sold': 'Sold'
  };
  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
};

const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'blocked': 'text-red-600',
    'lost': 'text-orange-600',
    'retired': 'text-gray-600',
    'in_repair': 'text-yellow-600',
    'sold': 'text-green-600'
  };
  return colorMap[status] || 'text-gray-900';
};

const DeviceStats: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && ['7d', '30d', '365d', 'this_month', 'this_year', 'custom'].includes(p)) {
      return p as PeriodType;
    }
    if (searchParams.get('start') && searchParams.get('end')) {
      return 'custom';
    }
    const saved = getUserPreference('deviceStats', 'period', '30d');
    if (['7d', '30d', '365d', 'this_month', 'this_year'].includes(saved)) {
      return saved as PeriodType;
    }
    return '30d';
  });
  
  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');
  
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      let url = `/api/stats/devices?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `/api/stats/devices?start=${customStart}&end=${customEnd}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching device stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch device statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, customStart, customEnd]);

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setUserPreference('deviceStats', 'period', newPeriod);
    }
    
    const params = new URLSearchParams();
    if (newPeriod === 'custom' && start && end) {
      params.set('start', start);
      params.set('end', end);
      setCustomStart(start);
      setCustomEnd(end);
    } else {
      params.set('period', newPeriod);
      setCustomStart('');
      setCustomEnd('');
    }
    setSearchParams(params);
  };

  // Aggregate status changes by user for display
  const getUserStatusSummary = () => {
    if (!stats) return [];
    
    const userMap = new Map<string, { user_name: string; user_id: number | null; statuses: Record<string, number>; total: number }>();
    
    stats.status_changes_by_user.forEach(row => {
      const key = row.user_name;
      if (!userMap.has(key)) {
        userMap.set(key, { user_name: row.user_name, user_id: row.user_id, statuses: {}, total: 0 });
      }
      const user = userMap.get(key)!;
      user.statuses[row.new_status] = (user.statuses[row.new_status] || 0) + row.count;
      user.total += row.count;
    });
    
    return Array.from(userMap.values()).sort((a, b) => b.total - a.total);
  };

  const SkeletonTable = () => (
    <div className="bg-white rounded-lg shadow animate-pulse">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="h-6 bg-gray-200 rounded w-48"></div>
      </div>
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-4">
            <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
            <div className="w-12 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Device Statistics"
        subtitle="Detailed breakdown of device creation and status changes"
        onBack={() => window.history.back()}
      />

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <SkeletonTable />
          <SkeletonTable />
          <SkeletonTable />
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Total Devices Created</h3>
                <p className="text-3xl font-bold text-naviter-blue">{stats.total_created}</p>
              </div>
            </div>
          </div>

          {/* Devices Created by User */}
          <UserBreakdownTable
            title="Devices Created by User"
            data={stats.created_by_user}
            emptyMessage="No devices were created in this period"
          />

          {/* Devices Created by Product */}
          <ProductBreakdownTable
            title="Devices Created by Product Type"
            data={stats.created_by_product}
            emptyMessage="No devices were created in this period"
          />

          {/* Status Changes from Active */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Status Changes from Active</h3>
              <p className="text-sm text-gray-500 mt-1">
                Devices that changed from Active status to another status (and are still in that status)
              </p>
            </div>
            
            {stats.status_changes_from_active.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No status changes from Active in this period
              </div>
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stats.status_changes_from_active.map(item => (
                    <div 
                      key={item.current_status}
                      className="bg-gray-50 rounded-lg p-4 text-center"
                    >
                      <p className={`text-2xl font-bold ${getStatusColor(item.current_status)}`}>
                        {item.count}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatStatus(item.current_status)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Status Changes by User */}
          {getUserStatusSummary().length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Status Changes by User</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Who changed devices from Active to other statuses
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Blocked
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lost
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Retired
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Other
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getUserStatusSummary().map((user, index) => (
                      <tr key={user.user_id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.user_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">
                          {user.statuses['blocked'] || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-orange-600">
                          {user.statuses['lost'] || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                          {user.statuses['retired'] || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                          {Object.entries(user.statuses)
                            .filter(([k]) => !['blocked', 'lost', 'retired'].includes(k))
                            .reduce((sum, [_, v]) => sum + v, 0) || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                          {user.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default DeviceStats;

